import { useField } from 'formik';
import { FormsGroup } from './FormsGroup';
import { useFormLabel } from './FormsI18nContext';
import React, { useEffect, useMemo } from 'react';
import { Input, InputProps } from 'antd';
import Dictaphone, { VoiceProps } from '../Dictaphone/Dictaphone';

export type FormsInputProps = VoiceProps & {
  name: string;
  label?: React.ReactNode;
  type?: InputProps['type'];
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  helpMessage?: string;
  mode?: 'touched' | 'always';
};

export const FormsInput = ({
  name,
  type,
  label: labelOverride,
  required,
  disabled,
  className,
  placeholder,
  helpMessage,
  enableVoice,
  activeVoiceId,
  mode,
  setActiveVoiceId,
}: FormsInputProps) => {
  const [field, , helpers] = useField(name);
  const label = useFormLabel(name, labelOverride);

  function handleOnBlur(e: React.ChangeEvent<HTMLInputElement>) {
    if (field.value) {
      const value = e.target.value?.trim();
      helpers.setValue(value);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    if (value?.length === 0) {
      helpers.setValue(null);
    } else {
      field.onChange(e);
    }
  }

  const dictaphoneId = useMemo(() => `dictaphone-${name}`, [name]);

  useEffect(() => {
    const value = field.value?.trim();
    helpers.setValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleTranscript(text: string) {
    if (text && field.value !== text)
      helpers.setValue(((field.value ?? '') + ' ' + text).trim());
  }

  return (
    <FormsGroup
      label={label}
      name={name}
      required={required}
      helpMessage={helpMessage}
      mode={mode}
    >
      {type === 'password' ? (
        <Input.Password
          type={type}
          {...field}
          value={field.value ?? ''}
          onChange={handleChange}
          onBlur={handleOnBlur}
          className={className}
          disabled={disabled}
          placeholder={placeholder}
        />
      ) : (
        <Input
          type={type}
          {...field}
          value={field.value ?? ''}
          onChange={handleChange}
          onBlur={handleOnBlur}
          className={className}
          disabled={disabled}
          placeholder={placeholder}
          addonAfter={
            enableVoice &&
            !disabled &&
            setActiveVoiceId && (
              <Dictaphone
                onTranscript={handleTranscript}
                setActiveVoiceId={setActiveVoiceId}
                activeVoiceId={activeVoiceId ?? null}
                id={dictaphoneId}
              />
            )
          }
        />
      )}
    </FormsGroup>
  );
};
